/**
 * ─── HTTP 基础请求库 ───────────────────────────────────────────────────────────
 *
 * 设计目标：
 *   1. 统一标配：credentials、Content-Type、params 序列化
 *   2. 业务码校验可配置（code），不同后端创建不同实例
 *   3. 两个拦截入口：onRequest（注入公共头/traceId）、onResponse（日志/监控）
 *   4. codeHandlers：按错误码注册处理函数（如 401 跳登录、403 弹提示）
 *   5. onError：兜底错误处理（网络异常 / HTTP 错误 / 未处理的业务错误码）
 */

import { handleSessionExpiredMsg } from './sessionExpiry';

// ─── 类型 ─────────────────────────────────────────────────────────────────────

/** 后端统一响应信封 */
export interface ApiEnvelope<T = unknown> {
  code: number;
  msg: string;
  logid?: string;
  data: T;
}

/** 单次请求额外选项（扩展原生 RequestInit） */
export type HttpOptions = Omit<RequestInit, 'body'> & {
  /** URL 查询参数，自动 encode 后拼接到 URL */
  params?: Record<string, string | number | boolean | undefined>;
};

/** 实例级配置 */
export interface RequestConfig {
  /** 请求根路径，最终 URL = baseURL + path */
  baseURL?: string;

  /**
   * 业务成功码（默认 0），支持多个
   * 例：code: 0  或  code: [0, 200]
   */
  code?: number | number[];

  /**
   * 请求拦截器
   * 在 fetch 发出前执行，可修改 url / init（添加公共请求头、traceId 等）
   * 返回 [newUrl, newInit] 或不返回（使用原值）
   */
  onRequest?: (
    url: string,
    init: RequestInit,
  ) => [string, RequestInit] | Promise<[string, RequestInit]> | void | Promise<void>;

  /**
   * 响应拦截器
   * 在业务码校验前执行，可做日志上报、全局 loading 关闭等
   * 不影响后续流程
   */
  onResponse?: (envelope: ApiEnvelope, url: string) => void | Promise<void>;

  /**
   * 业务错误码处理表
   * key = 错误码，value = 处理函数
   * - 返回 false：阻止后续抛出异常（由处理函数自行处置）
   * - 其他返回值：处理后继续抛出异常
   *
   * 示例：
   *   codeHandlers: {
   *     401: () => { window.location.href = '/login'; return false; },
   *     403: (env) => { message.error(env.msg); },
   *   }
   */
  codeHandlers?: Partial<Record<number, (envelope: ApiEnvelope) => boolean | void>>;

  /**
   * 兜底错误处理
   * 触发时机：网络错误 / HTTP 非 2xx / 未被 codeHandlers 阻断的业务错误
   */
  onError?: (error: Error, url: string) => void;
}

// ─── 内部核心逻辑 ─────────────────────────────────────────────────────────────

function resolveSuccessCodes(config: RequestConfig): number[] {
  const raw = config.code ?? 0;
  return Array.isArray(raw) ? raw : [raw];
}

function buildUrl(path: string, params?: HttpOptions['params']): string {
  if (!params) return path;
  const defined = Object.entries(params).filter(([, v]) => v !== undefined);
  if (!defined.length) return path;
  return `${path}?${new URLSearchParams(defined as [string, string][])}`;
}

async function coreRequest<T>(
  path: string,
  init: RequestInit,
  config: RequestConfig,
): Promise<T> {
  const baseUrl = config.baseURL ? config.baseURL.replace(/\/$/, '') : '';
  let url = baseUrl + path;

  // ── 请求拦截 ──────────────────────────────────────────────────────────────
  if (config.onRequest) {
    const result = await config.onRequest(url, init);
    if (result) [url, init] = result;
  }

  // ── 发起请求 ──────────────────────────────────────────────────────────────
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    config.onError?.(error, url);
    throw error;
  }

  if (!res.ok) {
    const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
    config.onError?.(error, url);
    throw error;
  }

  // ── 解析响应 ──────────────────────────────────────────────────────────────
  const envelope: ApiEnvelope<T> = await res.json();

  // ── 响应拦截 ──────────────────────────────────────────────────────────────
  await config.onResponse?.(envelope, url);

  // ── 会话失效拦截（还原老项目 authFetch 行为）─────────────────────────────
  if (handleSessionExpiredMsg(envelope.msg)) {
    throw new Error(envelope.msg);
  }

  // ── 业务码校验 ────────────────────────────────────────────────────────────
  if (!resolveSuccessCodes(config).includes(envelope.code)) {
    const handler = config.codeHandlers?.[envelope.code];
    const suppress = handler?.(envelope as ApiEnvelope) === false;
    if (!suppress) {
      const error = new Error(envelope.msg ?? `业务错误码: ${envelope.code}`);
      config.onError?.(error, url);
      throw error;
    }
  }

  return envelope.data;
}

// ─── Request 类 ───────────────────────────────────────────────────────────────

export class Request {
  private config: RequestConfig;

  constructor(config: RequestConfig = {}) {
    this.config = { ...config };
  }

  /**
   * 运行时更新实例配置（merge，不替换）
   * 适合在 app.ts 初始化后追加 onRequest / codeHandlers 等
   */
  configure(patch: Partial<RequestConfig>): void {
    Object.assign(this.config, patch);
  }

  async get<T = unknown>(url: string, options: HttpOptions = {}): Promise<T> {
    const { params, ...rest } = options;
    return coreRequest<T>(buildUrl(url, params), {
      method: 'GET',
      credentials: 'include',
      ...rest,
    }, this.config);
  }

  async post<T = unknown>(url: string, body: object = {}, options: HttpOptions = {}): Promise<T> {
    const { params, headers, ...rest } = options;
    return coreRequest<T>(buildUrl(url, params), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      ...rest,
    }, this.config);
  }

  async put<T = unknown>(url: string, body: object = {}, options: HttpOptions = {}): Promise<T> {
    const { params, headers, ...rest } = options;
    return coreRequest<T>(buildUrl(url, params), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      ...rest,
    }, this.config);
  }

  async delete<T = unknown>(url: string, options: HttpOptions = {}): Promise<T> {
    const { params, ...rest } = options;
    return coreRequest<T>(buildUrl(url, params), {
      method: 'DELETE',
      credentials: 'include',
      ...rest,
    }, this.config);
  }
}

// ─── 工厂函数 & 默认实例 ──────────────────────────────────────────────────────

/**
 * 创建独立配置的请求实例
 * 适用于对接不同后端服务（不同成功码、不同错误处理策略）
 *
 * @example
 * // mrtc 服务 code===200 为成功
 * export const mrtcRequest = createRequest({ code: 200 });
 */
export function createRequest(config: RequestConfig = {}): Request {
  return new Request(config);
}

/**
 * 默认实例 — 标准 TOS 后端（code === 0 为成功）
 *
 * 在 src/app.ts 中通过 request.configure({...}) 注入全局拦截器：
 * @example
 * import request from '@/utils/request';
 * request.configure({
 *   onRequest: (url, init) => {
 *     init.headers = { ...init.headers, 'X-App-Version': '1.0.0' };
 *     return [url, init];
 *   },
 *   codeHandlers: {
 *     401: () => { window.location.href = '/login'; return false; },
 *   },
 * });
 */
const request = createRequest({ code: 0 });
export default request;
